import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';

import { Blocks } from '@/components/Blocks';
import { Icon } from '@/components/Icon';
import { SearchBar } from '@/components/SearchBar';
import { CarouselBlock } from '@/components/carouselsBlocks';
import { useTrackSearch } from '@/modules/analytic/hooks/useTrackSearch';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useCategories } from '@/modules/categories/hooks/useCategories';
import { CategoriesList } from '@/modules/categories/presentation/CategoriesList';
import { useHomepage } from '@/modules/homepage/hooks/useHomepage';
import { HomeLoader } from '@/modules/homepage/presentation/HomeLoader';
import { useNotifications } from '@/modules/notifications/hooks/useNotifications';
import { OnboardingModal } from '@/modules/onboarding/presentation/modals/OnboardingModal';
import { useSearch } from '@/modules/search/hooks/useSearch';
import {
  SearchAutoSuggestList,
  SearchErrorMessage,
  SearchMediasBlock,
  SearchRolesBlock,
} from '@/modules/search/presentation';
import { ContentTypes, Media, Program, Role } from '@/types';
import { RoleWithPrograms, SearchTypes } from '@/types/search';
import { queryClient } from '@/utils/request';

const HubPage: NextPage = () => {
  const { user } = useAuth();
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { categories } = useCategories();

  const { showNotificationError } = useNotifications();

  const [displayCategories, setDisplayCategories] = useState(false);
  const [displayHomepage, setDisplayHomepage] = useState(true);

  // Search
  const [searchText, setSearchText] = useState('');
  const [searchTextSubmitted, setSearchTextSubmitted] = useState('');
  const [autoSuggestvalue, setAutoSuggestvalue] = useState('');
  const [roleWithPrograms, setRoleWithPrograms] = useState<RoleWithPrograms>(null);
  const [debouncedAutoSuggestvalue] = useDebounce(autoSuggestvalue, 500);
  const [debouncedSearchText] = useDebounce(searchText, 500);
  const { trackSearch } = useTrackSearch();

  const handleOnChange = (searchValue: string) => {
    if (searchTextSubmitted !== searchText) {
      setSearchTextSubmitted('');
    }
    if (searchValue !== autoSuggestvalue) {
      setAutoSuggestvalue(searchValue);
    }
    setSearchText(searchValue);
  };

  const handleOnFocus = useCallback(() => {
    if (searchText || searchTextSubmitted) return;

    setDisplayCategories(true);
    setDisplayHomepage(false);
  }, [searchText, searchTextSubmitted]);

  const handleOnBlur = useCallback(() => {
    if (searchText || searchTextSubmitted) return;

    setDisplayCategories(false);
    setDisplayHomepage(true);
  }, [searchText, searchTextSubmitted]);

  const handleOnSubmit = (search: string) => {
    setSearchText(search);
    setSearchTextSubmitted(search);
    setRoleWithPrograms(null);

    const params = new URL(document.location.toString()).searchParams;
    params.set('q', search);
    params.set('s', 'true');
    router.push(`${router.pathname}?${params.toString()}`);
  };

  const handleOnReset = () => {
    setSearchText('');
    setSearchTextSubmitted('');
    setRoleWithPrograms(null);
    setDisplayCategories(false);
    setDisplayHomepage(true);
    trackSearch({ value: searchText });
  };

  const {
    isLoadingSearch,
    searchPopularPrograms,
    searchAutoSuggestList,
    searchResults,
    errorFetchSearchResults,
  } = useSearch({
    autoSuggestvalue: debouncedAutoSuggestvalue,
    searchText: debouncedSearchText,
  });

  useEffect(() => {
    if (!errorFetchSearchResults) return;

    showNotificationError('search.error.notifications');
  }, [errorFetchSearchResults, showNotificationError]);

  const hasSearchResults = useMemo(
    () => !!Object.values(searchResults || {})?.length,
    [searchResults]
  );

  const { homepage, isLoadingHomepage } = useHomepage({ accountType: 'core' });

  const corpoHomepage = useHomepage({
    accountType: 'corporate',
    homepageId: user?.corpoHomePageID,
  });
  const [openOnboardingModal, setOpenOnboardingModal] = useState(false);

  /** This saves (in useQuery cache) each program from the homepage with the correct source property */
  useEffect(() => {
    if (isLoadingHomepage) return;

    if (homepage) {
      homepage.blocks.forEach((block) => {
        if (block.contentType === ContentTypes.Program) {
          block.items.forEach((item) => {
            queryClient.setQueryData(['program', item.UUID], item);
          });
        }
      });
    }
  }, [homepage, isLoadingHomepage]);

  const handleOpenOnboardingModal = useCallback(() => {
    setOpenOnboardingModal(true);
  }, []);

  const handleCloseOnboardingModal = useCallback(() => {
    setOpenOnboardingModal(false);
  }, []);

  useEffect(() => {
    if (!user?.UUID || user?.categories?.length >= 2) return;
    handleOpenOnboardingModal();
  }, [user?.UUID, user?.categories, handleOpenOnboardingModal]);

  const handleDisplayRole = (roleWithPrograms: RoleWithPrograms) => {
    setRoleWithPrograms(roleWithPrograms);
    trackSearch({
      value: searchText,
      terminationReason: SearchTypes.Role,
      contentUUID: roleWithPrograms.UUID,
    });
  };

  const handleTrackSearch = (terminationReason: SearchTypes, UUID: string) => {
    trackSearch({ value: searchText, terminationReason, contentUUID: UUID });
  };

  const renderSearchBlocks = (
    type: SearchTypes,
    blocks: (Program | Media | Role)[],
    handleTrackSearch: (terminationReason: SearchTypes, UUID: string) => void
  ) => {
    switch (type) {
      case SearchTypes.Program: {
        return (
          <CarouselBlock
            block={{
              items: blocks as Program[],
              title: formatMessage({ id: 'search.results.program.title' }),
              size: 'default',
            }}
          />
        );
      }

      case SearchTypes.Media:
        return (
          <SearchMediasBlock
            block={{
              items: blocks as Media[],
              title: formatMessage({ id: 'search.results.media.title' }),
            }}
            onClick={(e) => handleTrackSearch(SearchTypes.Media, e)}
          />
        );

      case SearchTypes.Role: {
        return (
          <SearchRolesBlock
            block={{
              items: blocks as Role[],
              title: formatMessage({ id: 'search.results.role.title' }),
            }}
            onClick={handleDisplayRole}
          />
        );
      }
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="content">
      <h1 className="title-h1 px-5 py-10">{formatMessage({ id: 'layout.nav.home' })}</h1>
      <div className="w-full">
        <SearchBar
          searchText={searchText}
          setSearchText={setSearchText}
          onChange={handleOnChange}
          onSubmit={handleOnSubmit}
          onReset={handleOnReset}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          placeholder={formatMessage({ id: 'common.form.field.placeholder.search' })}
          initialSearch
        />
      </div>

      {!searchTextSubmitted ? (
        <>
          {searchAutoSuggestList ? (
            <SearchAutoSuggestList
              searchAutoSuggestList={searchAutoSuggestList}
              inputChange={searchText}
              onClickItem={handleOnSubmit}
            />
          ) : (
            !displayCategories &&
            searchPopularPrograms && (
              <CarouselBlock
                block={{
                  items: searchPopularPrograms,
                  title: 'Les plus recherchés',
                  size: 'default',
                }}
              />
            )
          )}
        </>
      ) : (
        <>
          {hasSearchResults ? (
            <>
              {!roleWithPrograms ? (
                <div className="flex flex-wrap">
                  {Object.entries(searchResults).map(([type, searchResult]) =>
                    !searchResult?.length ? (
                      <div key={type} className="blocks w-full">
                        <h2 className="title">
                          {formatMessage({ id: `search.results.${type}.title` })}
                        </h2>
                        <div className="p-5 text-brand-neutral-700">
                          {formatMessage({ id: 'search.emptyView.title' })}
                        </div>
                      </div>
                    ) : (
                      <div key={type} className="w-full overflowY-auto h-auto">
                        {renderSearchBlocks(type as SearchTypes, searchResult, handleTrackSearch)}
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div>
                  <div
                    className="flex items-center my-5 cursor-pointer"
                    onClick={() => setRoleWithPrograms(null)}
                  >
                    <Icon name="chevron-left" className="text-4xl ml-3" />
                    <p>{formatMessage({ id: 'search.back' })}</p>
                  </div>

                  <CarouselBlock
                    block={{
                      items: roleWithPrograms?.programs,
                      title: roleWithPrograms?.name,
                      size: 'default',
                    }}
                    key={roleWithPrograms.UUID}
                  />
                </div>
              )}
            </>
          ) : (
            !isLoadingSearch && <SearchErrorMessage searchText={searchText} />
          )}
        </>
      )}

      {!hasSearchResults && displayCategories && (
        <>
          <h2 className="title-h2 px-5">{formatMessage({ id: 'metas.page.categories' })}</h2>
          <CategoriesList categories={categories} />
        </>
      )}

      {isLoadingHomepage || corpoHomepage.isLoadingHomepage ? (
        <HomeLoader />
      ) : (
        !hasSearchResults &&
        displayHomepage && (
          <div className="w-full h-full">
            {corpoHomepage.homepage && <Blocks blocks={corpoHomepage.homepage?.blocks} />}
            {openOnboardingModal && <OnboardingModal close={handleCloseOnboardingModal} />}
            <Blocks blocks={homepage?.blocks} />
          </div>
        )
      )}
    </div>
  );
};

export default HubPage;
